import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-webgl';
import * as bodySegmentation from '@tensorflow-models/body-segmentation';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { Sprite, RenderTexture, Filter, Container, Texture, Renderer, autoDetectRenderer } from 'pixi.js';
import { LogService } from 'app/services/logging.service';

/**
 * Service for handling background segmentation and foreground isolation
 * for video effects that should only apply to the background
 */
@Injectable({
  providedIn: 'root',
})
export class BackgroundSegmentationService implements OnDestroy {
  private ngZone = inject(NgZone);
  private logService = inject(LogService);

  // The loaded body segmentation model
  private segmenter: bodySegmentation.BodySegmenter | null = null;

  // Flag to track if the model is loading
  private isLoading = false;

  // Subject to track model loading state
  private modelLoaded$ = new BehaviorSubject<boolean>(false);

  // Segmentation mask texture
  private maskTexture: RenderTexture | null = null;

  // Temporary canvas for segmentation operations
  private canvas: HTMLCanvasElement | null = null;

  // Configuration for segmentation
  private segmentationConfig = {
    flipHorizontal: false,
    multiSegmentation: false,
    // Lower threshold to better match the test implementation
    segmentationThreshold: 0.2, // Changed from 0.3 to be more sensitive
    maxDetections: 1,
  };

  // Output resolution of segmentation mask
  private outputWidth = 256;
  private outputHeight = 256;

  // Tracking memory cleanup
  private memoryCleanupInterval = 30000; // 30 seconds
  private memoryCleanupSubscription: Subscription | null = null;
  private frameCount = 0;
  private readonly FRAMES_BEFORE_CLEANUP = 100;

  // Track whether service is active to avoid processing when not needed
  private isActive = false;

  // Add a property to track error state
  private isErrorState = false;

  public constructor() {
    // Load the model when the service is created
    void this.loadModel();

    // Set up periodic memory cleanup
    this.memoryCleanupSubscription = interval(this.memoryCleanupInterval).subscribe(() => {
      this.performMemoryCleanup();
    });
  }

  /**
   * Gets an observable for model loading state
   */
  public get modelLoaded(): Observable<boolean> {
    return this.modelLoaded$.asObservable();
  }

  /**
   * Activates the service - call this when segmentation is needed
   */
  public activate(): void {
    this.isActive = true;
  }

  /**
   * Deactivates the service - call this when segmentation is no longer needed
   * to reduce resource usage
   */
  public deactivate(): void {
    this.isActive = false;
    // Perform a cleanup when deactivated
    this.performMemoryCleanup();
  }

  /**
   * Perform memory cleanup to avoid memory leaks in TensorFlow.js
   * Ensures proper disposal of tensors and other GPU resources
   */
  private performMemoryCleanup(): void {
    try {
      // Check if TensorFlow is available
      if (!tf || typeof tf.disposeVariables !== 'function') {
        this.logService.warn('BGSEG: TensorFlow not available for cleanup');
        return;
      }

      this.logService.info('BGSEG: Performing memory cleanup');

      // Dispose of all tensors in the backend to free memory
      tf.engine().endScope();
      tf.engine().startScope();

      // Force garbage collection of tensors
      tf.tidy(() => {
        //
      });

      // Log memory status after cleanup
      const memoryInfo = tf.memory();
      this.logService.info(
        `BGSEG: Memory after cleanup - Tensors: ${memoryInfo.numTensors}, Bytes: ${memoryInfo.numBytes}`,
      );
    } catch (error) {
      this.logService.error('BGSEG: Error during memory cleanup:', error);
    }
  }

  /**
   * Load the body segmentation model
   */
  private async loadModel(): Promise<void> {
    if (this.segmenter || this.isLoading) {
      return;
    }

    this.isLoading = true;

    try {
      // Ensure TensorFlow.js is using WebGL backend for best performance
      await tf.setBackend('webgl');

      // Set memory growth to true to avoid OOM errors
      tf.env().set('WEBGL_DELETE_TEXTURE_THRESHOLD', 0);
      tf.env().set('WEBGL_FORCE_F16_TEXTURES', true);

      // Warm up the model - this can help with initial segmentation accuracy
      await tf.ready();

      // Load the segmentation model with MediaPipe configuration
      this.segmenter = await bodySegmentation.createSegmenter(
        bodySegmentation.SupportedModels.MediaPipeSelfieSegmentation,
        {
          runtime: 'tfjs',
          // 'general' is better for faces and upper body detection
          modelType: 'general',
        },
      );

      // Initialize canvas for segmentation operations
      this.canvas = document.createElement('canvas');
      this.canvas.width = this.outputWidth;
      this.canvas.height = this.outputHeight;

      // Create a mask texture
      this.maskTexture = RenderTexture.create({
        width: this.outputWidth,
        height: this.outputHeight,
      });

      this.ngZone.run(() => {
        this.modelLoaded$.next(true);
        this.isLoading = false;
      });
    } catch (error) {
      this.logService.error('Failed to load segmentation model', error);
      this.isLoading = false;
      this.ngZone.run(() => {
        this.modelLoaded$.next(false);
      });
    }
  }

  /**
   * Segment a video frame to identify person vs background
   * @param imageSource The video or image source to segment
   * @returns Promise with segmentation mask or null if failed
   */
  public async segmentFrame(imageSource: HTMLVideoElement | HTMLImageElement): Promise<ImageData | null> {
    if (!this.isActive || !this.segmenter || !imageSource || !imageSource.width || !imageSource.height) {
      return null;
    }

    try {
      // Check if TensorFlow.js is ready
      if (!tf.getBackend()) {
        this.logService.warn('BGSEG: TensorFlow backend not available');
        this.isErrorState = true;
        return null;
      }

      // Reset error state if we've recovered
      if (this.isErrorState) {
        this.isErrorState = false;
      }

      // Increment frame counter for memory cleanup scheduling
      this.frameCount++;
      if (this.frameCount > this.FRAMES_BEFORE_CLEANUP) {
        this.frameCount = 0;
        this.performMemoryCleanup();
      }

      // Perform segmentation with MediaPipe
      const segmentation = await this.segmenter.segmentPeople(imageSource, {
        flipHorizontal: this.segmentationConfig.flipHorizontal,
        multiSegmentation: this.segmentationConfig.multiSegmentation,
        segmentationThreshold: this.segmentationConfig.segmentationThreshold,
        maxDetections: this.segmentationConfig.maxDetections,
      });

      if (!segmentation || segmentation.length === 0) {
        this.logService.warn('BGSEG: No segmentation results detected');
        return null;
      }

      // We only use the first person's segmentation (based on maxDetections: 1 config)
      const firstPersonSegmentation = segmentation[0];

      // Check if mask creation can be done safely
      let maskImageData: ImageData | null = null;
      try {
        // First, try using the built-in toImageData method
        const mask = await firstPersonSegmentation.mask.toImageData();
        if (mask) {
          maskImageData = mask;
        }
      } catch (maskError) {
        this.logService.warn('BGSEG: Error in toImageData, using fallback', maskError);
      }

      // If standard method failed, use fallback
      if (!maskImageData) {
        try {
          // Get the raw mask data
          const maskTensor = firstPersonSegmentation.mask;
          const maskData = await (await maskTensor.toTensor()).data();
          const height = 255; //firstPersonSegmentation.mask.getHeight();
          const width = 255; //firstPersonSegmentation.mask.getWidth();

          maskImageData = this.createFallbackMask(maskData as Float32Array, width, height);
          this.logService.info('BGSEG: Created fallback mask successfully');
        } catch (fallbackError) {
          this.logService.error('BGSEG: Fallback mask creation failed:', fallbackError);
          return null;
        }
      }

      // If we still don't have a valid mask, return null
      if (!maskImageData) {
        return null;
      }

      // Enhance mask contrast for better visual results
      this.enhanceMaskContrast(maskImageData);

      return maskImageData;
    } catch (error) {
      this.logService.error('BGSEG: Segmentation error:', error);
      this.isErrorState = true;

      // Try to recover from TensorFlow backend errors
      if (error instanceof Error && (error.message.includes('backend') || error.message.includes('tensor'))) {
        this.logService.warn('BGSEG: Trying to recover from TensorFlow error');
        this.performMemoryCleanup();

        // Try to reset the TensorFlow.js backend
        try {
          await tf.setBackend('webgl');
          await tf.ready();
          this.logService.info('BGSEG: Successfully reset TensorFlow backend');
        } catch (resetError) {
          this.logService.error('BGSEG: Failed to reset TensorFlow backend:', resetError);
        }
      }

      return null;
    }
  }

  /**
   * Creates a fallback mask when toImageData fails
   * This is a safety measure to handle TensorFlow.js backend errors
   * @param foregroundScores The raw segmentation data array
   * @param width Width of the segmentation
   * @param height Height of the segmentation
   * @returns ImageData object or null if creation fails
   */
  private createFallbackMask(foregroundScores: Float32Array, width: number, height: number): ImageData | null {
    try {
      if (!foregroundScores || foregroundScores.length === 0) {
        return null;
      }

      this.logService.info(`BGSEG: Creating fallback mask (${width}x${height})`);

      // Create RGBA data for the mask
      const data = new Uint8ClampedArray(width * height * 4);
      const threshold = this.segmentationConfig.segmentationThreshold;

      // Fill the mask based on foreground scores
      for (let i = 0; i < foregroundScores.length; i++) {
        const pixelIndex = i * 4;
        // Apply binary threshold to foreground scores
        const value = foregroundScores[i] > threshold ? 255 : 0;

        data[pixelIndex] = value; // R
        data[pixelIndex + 1] = value; // G
        data[pixelIndex + 2] = value; // B
        data[pixelIndex + 3] = 255; // A (always fully opaque)
      }

      return new ImageData(data, width, height);
    } catch (error) {
      this.logService.error('BGSEG: Error in fallback mask creation:', error);
      return null;
    }
  }

  /**
   * Enhances the contrast of the segmentation mask for better visual results
   * @param imageData The mask image data to enhance
   */
  private enhanceMaskContrast(imageData: ImageData): void {
    try {
      if (!imageData || !imageData.data) {
        return;
      }

      // Apply a binary threshold to ensure clear separation
      const threshold = 128; // Middle value for 8-bit color

      for (let i = 0; i < imageData.data.length; i += 4) {
        // Apply binary threshold (either 0 or 255, no in-between)
        const value = imageData.data[i] > threshold ? 255 : 0;

        // Set RGB to the same value (keep it grayscale)
        imageData.data[i] = value; // R
        imageData.data[i + 1] = value; // G
        imageData.data[i + 2] = value; // B
        // Keep alpha fully opaque
        imageData.data[i + 3] = 255; // A
      }
    } catch (error) {
      this.logService.error('BGSEG: Error enhancing mask contrast:', error);
    }
  }

  /**
   * Updates the PixiJS mask texture based on segmentation data
   * @param imageData The segmentation mask as ImageData
   * @returns The updated RenderTexture
   */
  public updateMaskTexture(imageData: ImageData): RenderTexture | null {
    if (!this.canvas || !this.maskTexture) {
      return null;
    }

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    // Put the image data on the canvas - this transfers our binary mask
    ctx.putImageData(imageData, 0, 0);

    // Create a temporary sprite with the canvas as texture
    const tempSprite = Sprite.from(this.canvas);

    this.logService.info('BGSEG: Updating mask texture from canvas');

    // Create a fallback renderer if the app renderer isn't available
    let renderer = null;

    // Try to get the app renderer first
    // @ts-expect-error: `globalThis` is not defined in the current context
    const app = globalThis.__PIXI_APP__;
    if (app && app.renderer) {
      renderer = app.renderer;
      this.logService.info('BGSEG: Using global PIXI app renderer');
    } else {
      // If no global app renderer exists, create a temporary one
      this.logService.info('BGSEG: Creating temporary renderer for mask texture');
      renderer = autoDetectRenderer({
        width: this.maskTexture.width,
        height: this.maskTexture.height,
        backgroundAlpha: 0,
      });
    }

    // Now use the renderer (either global or temporary)
    if (renderer) {
      renderer.render(tempSprite, { renderTexture: this.maskTexture });
      this.logService.info('BGSEG: Successfully rendered mask to texture');
    } else {
      this.logService.error('BGSEG: Failed to create any valid renderer');
      return null;
    }

    // Clean up the temporary renderer if we created one
    if (renderer && renderer !== app?.renderer) {
      renderer.destroy();
      this.logService.info('BGSEG: Destroyed temporary renderer');
    }

    // Destroy the temporary sprite to avoid memory leaks
    tempSprite.destroy({ texture: false, children: true });

    return this.maskTexture;
  }

  /**
   * Creates a custom filter that applies effects only to the background
   * @param baseFilter The filter to apply to the background
   * @param maskTexture The segmentation mask texture
   * @returns A new PIXI filter that combines the original image with filtered background
   */
  public createBackgroundOnlyFilter(baseFilter: Filter, maskTexture: Texture): Filter {
    // Create a shader that applies the filter only to the background
    const fragmentShader = `
      varying vec2 vTextureCoord;
      uniform sampler2D uSampler;         // Original texture
      uniform sampler2D uFilteredSampler; // Filtered texture
      uniform sampler2D uMaskSampler;     // Segmentation mask
      uniform float uThreshold;           // Configurable threshold
      
      void main(void) {
        vec4 original = texture2D(uSampler, vTextureCoord);
        vec4 filtered = texture2D(uFilteredSampler, vTextureCoord);
        vec4 maskColor = texture2D(uMaskSampler, vTextureCoord);
        
        // Use binary threshold for mask - person is white (1.0), background is black (0.0)
        float maskValue = maskColor.r;
        
        // Use the configurable threshold
        gl_FragColor = maskValue > uThreshold ? original : filtered;
      }
    `;

    // Use a much smaller texture size - matching a smaller percentage of PLAYER_SIZE
    // to solve the issue with texture being too large
    const filteredTexture = RenderTexture.create({
      width: 120, // Reduced from 220
      height: 120, // Reduced from 220
      resolution: 1,
    });

    // Create the composite filter with the shader
    const compositeFilter = new Filter(undefined, fragmentShader, {
      uFilteredSampler: filteredTexture,
      uMaskSampler: maskTexture,
      uThreshold: 0.1, // Default threshold value, can be updated later
    });

    // Store references for later use
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (compositeFilter as any).baseFilter = baseFilter;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (compositeFilter as any).filteredTexture = filteredTexture;

    return compositeFilter;
  }

  /**
   * Updates the background filter texture before rendering
   * This method must be called every frame before applying the composite filter
   * @param sprite The sprite that the filter is applied to
   * @param compositeFilter The composite filter created by createBackgroundOnlyFilter
   * @param renderer The PIXI renderer
   */
  public updateBackgroundFilter(sprite: Sprite, compositeFilter: Filter, renderer: Renderer): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!compositeFilter || !(compositeFilter as any).baseFilter || !(compositeFilter as any).filteredTexture) {
      this.logService.warn('BGSEG: Invalid composite filter, cannot update');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseFilter = (compositeFilter as any).baseFilter;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filteredTexture = (compositeFilter as any).filteredTexture;

    // Create a fresh sprite with the original texture
    const tempSprite = new Sprite(sprite.texture);

    // Match the size of the tempSprite to the filteredTexture dimensions
    // which is now 120x120 instead of 220x220
    tempSprite.width = 120;
    tempSprite.height = 120;

    // Set anchor to center
    tempSprite.anchor.set(0.5, 0.5);

    // Apply mirroring if present in original sprite (important for primary player)
    tempSprite.scale.x = Math.sign(sprite.scale.x) * Math.abs(tempSprite.scale.x);

    // Apply the blur filter to the sprite
    tempSprite.filters = [baseFilter];

    // Position in the center of the texture
    const container = new Container();
    container.position.set(60, 60); // Half of the 120px texture size
    container.addChild(tempSprite);

    // Clear and render to the texture
    renderer.render(container, { renderTexture: filteredTexture, clear: true });

    // Update filter uniforms
    if (compositeFilter.uniforms.uMaskSampler && this.maskTexture) {
      compositeFilter.uniforms.uMaskSampler = this.maskTexture;
    }
    if (compositeFilter.uniforms.uFilteredSampler) {
      compositeFilter.uniforms.uFilteredSampler = filteredTexture;
    }

    // Clean up
    container.destroy({ children: true });
  }

  /**
   * Helper method to resize a segmentation mask array
   */
  private resizeSegmentationMask(
    mask: Uint8Array,
    origWidth: number,
    origHeight: number,
    targetWidth: number,
    targetHeight: number,
  ): Uint8Array {
    if (origWidth === targetWidth && origHeight === targetHeight) {
      return mask;
    }

    const resized = new Uint8Array(targetWidth * targetHeight);
    const xRatio = origWidth / targetWidth;
    const yRatio = origHeight / targetHeight;

    for (let y = 0; y < targetHeight; y++) {
      const origY = Math.floor(y * yRatio);

      for (let x = 0; x < targetWidth; x++) {
        const origX = Math.floor(x * xRatio);
        const origIndex = origY * origWidth + origX;
        const targetIndex = y * targetWidth + x;

        resized[targetIndex] = mask[origIndex];
      }
    }

    return resized;
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    // Clean up TensorFlow resources
    if (tf.getBackend() === 'webgl') {
      // Clean WebGL context to prevent memory leaks
      const engine = tf.engine();
      engine.disposeVariables();
      tf.dispose();
    }

    this.segmenter = null;

    // Destroy PixiJS resources
    if (this.maskTexture) {
      this.maskTexture.destroy();
      this.maskTexture = null;
    }

    // Clean up HTML elements
    if (this.canvas) {
      this.canvas = null;
    }

    // Clean up memory cleanup subscription
    if (this.memoryCleanupSubscription) {
      this.memoryCleanupSubscription.unsubscribe();
      this.memoryCleanupSubscription = null;
    }
  }

  /**
   * Angular lifecycle hook for cleanup when service is destroyed
   */
  public ngOnDestroy(): void {
    // Clean up memory cleanup subscription
    if (this.memoryCleanupSubscription) {
      this.memoryCleanupSubscription.unsubscribe();
      this.memoryCleanupSubscription = null;
    }

    // Perform a final cleanup
    this.performMemoryCleanup();

    // Call destroy to clean up all resources
    this.destroy();
  }
}
